// AM Advertising Home — wins & worries: accounts ranked by CPL CHANGE
// (movement, not absolute level) vs the comparison range. PURE + tested.
//
// ⚠️ VOLUME-GUARDED like the spike alert: BOTH windows need a real lead base
// and real spend, or a 2-lead account tops either list on noise.
//
// CPL here is per-account in the account's DOMINANT current-window currency
// (one account's channels bill in one currency in practice); cross-account
// blending never happens in this ranking — each entry is its own currency.

import type { SpineDay } from '../../types/advertisingHome.js';
import { classifyMover } from './noLeads.js';
import { RANK_MIN_LEADS, RANK_MIN_SPEND, RANK_TOP_N } from './guards.js';

export interface AccountCplChange {
  accountId: string;
  currency: string;
  cplCurrent: number;
  cplPrevious: number;
  /** Signed %; negative = CPL fell = improved (a win). */
  changePct: number;
  leadsCurrent: number;
  leadsPrevious: number;
  spendCurrent: number;
}

interface AccountWindow {
  spendByCurrency: Map<string, number>;
  leadsByCurrency: Map<string, number>;
}

function fold(rows: readonly SpineDay[]): Map<string, AccountWindow> {
  const byAccount = new Map<string, AccountWindow>();
  for (const row of rows) {
    let w = byAccount.get(row.accountId);
    if (!w) {
      w = { spendByCurrency: new Map(), leadsByCurrency: new Map() };
      byAccount.set(row.accountId, w);
    }
    w.spendByCurrency.set(row.currency, (w.spendByCurrency.get(row.currency) ?? 0) + row.spend);
    w.leadsByCurrency.set(row.currency, (w.leadsByCurrency.get(row.currency) ?? 0) + row.leads);
  }
  return byAccount;
}

function dominantCurrency(w: AccountWindow): string | null {
  let best: string | null = null;
  let max = -Infinity;
  for (const [c, amount] of w.spendByCurrency) {
    if (amount > max) {
      max = amount;
      best = c;
    }
  }
  return best;
}

const r2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Per-account CPL change between the current rows and the comparison rows
 * (either grain — dates are ignored, only account/currency sums matter).
 * Accounts failing the volume guard in EITHER window are excluded entirely.
 */
export function computeCplChanges(
  current: readonly SpineDay[],
  compare: readonly SpineDay[],
): AccountCplChange[] {
  const cur = fold(current);
  const prev = fold(compare);
  const out: AccountCplChange[] = [];

  for (const [accountId, w] of cur) {
    const p = prev.get(accountId);
    if (!p) continue;
    const currency = dominantCurrency(w);
    if (!currency) continue;
    const spendCur = w.spendByCurrency.get(currency) ?? 0;
    const leadsCur = w.leadsByCurrency.get(currency) ?? 0;
    const spendPrev = p.spendByCurrency.get(currency) ?? 0;
    const leadsPrev = p.leadsByCurrency.get(currency) ?? 0;

    // ⚠️ The volume guard — BOTH windows, leads AND spend.
    if (
      leadsCur < RANK_MIN_LEADS ||
      leadsPrev < RANK_MIN_LEADS ||
      spendCur < RANK_MIN_SPEND ||
      spendPrev < RANK_MIN_SPEND
    ) {
      continue;
    }

    const cplCurrent = spendCur / leadsCur;
    const cplPrevious = spendPrev / leadsPrev;
    if (cplPrevious <= 0) continue;
    out.push({
      accountId,
      currency,
      cplCurrent: r2(cplCurrent),
      cplPrevious: r2(cplPrevious),
      changePct: r2(((cplCurrent - cplPrevious) / cplPrevious) * 100),
      leadsCurrent: r2(leadsCur),
      leadsPrevious: r2(leadsPrev),
      spendCurrent: r2(spendCur),
    });
  }
  return out;
}

/**
 * A zero-lead TRANSITION — the one move a CPL ranking cannot express, because
 * CPL is undefined on the zero side. ⚠️ NOT subject to the lead-volume floors:
 * "leads → 0" is ALWAYS a negative move and "0 → some" a positive one
 * (noLeads.ts, classifyMover). Spend in the current window is still required
 * for leads_gone, so a channel that simply stopped spending is the no-spend
 * alert's business, not a mover.
 */
export interface AccountLeadTransition {
  accountId: string;
  currency: string;
  kind: 'leads_gone' | 'leads_back';
  leadsCurrent: number;
  leadsPrevious: number;
  spendCurrent: number;
  spendPrevious: number;
}

export function computeLeadTransitions(
  current: readonly SpineDay[],
  compare: readonly SpineDay[],
): AccountLeadTransition[] {
  const cur = fold(current);
  const prev = fold(compare);
  const out: AccountLeadTransition[] = [];
  for (const accountId of new Set([...cur.keys(), ...prev.keys()])) {
    const w = cur.get(accountId);
    const p = prev.get(accountId);
    const currency = (w && dominantCurrency(w)) ?? (p && dominantCurrency(p)) ?? null;
    if (!currency) continue;
    const after = {
      leads: w?.leadsByCurrency.get(currency) ?? 0,
      spend: w?.spendByCurrency.get(currency) ?? 0,
    };
    const before = {
      leads: p?.leadsByCurrency.get(currency) ?? 0,
      spend: p?.spendByCurrency.get(currency) ?? 0,
    };
    const kind = classifyMover(before, after);
    if (kind !== 'leads_gone' && kind !== 'leads_back') continue;
    out.push({
      accountId,
      currency,
      kind,
      leadsCurrent: r2(after.leads),
      leadsPrevious: r2(before.leads),
      spendCurrent: r2(after.spend),
      spendPrevious: r2(before.spend),
    });
  }
  return out;
}

export interface WinsAndWorries {
  /** Accounts whose leads went to 0 — worse than any CPL worry, most spend first. */
  leadsGone: AccountLeadTransition[];
  /** Accounts whose leads came back from 0 — the positive twin. */
  leadsBack: AccountLeadTransition[];
  /** Biggest CPL improvements (most negative change first). */
  wins: AccountCplChange[];
  /** Biggest CPL declines (most positive change first). */
  worries: AccountCplChange[];
}

export function rankCplChanges(
  current: readonly SpineDay[],
  compare: readonly SpineDay[],
  topN = RANK_TOP_N,
): WinsAndWorries {
  const changes = computeCplChanges(current, compare);
  const wins = changes
    .filter((c) => c.changePct < 0)
    .sort((a, b) => a.changePct - b.changePct)
    .slice(0, topN);
  const worries = changes
    .filter((c) => c.changePct > 0)
    .sort((a, b) => b.changePct - a.changePct)
    .slice(0, topN);
  const transitions = computeLeadTransitions(current, compare);
  const leadsGone = transitions
    .filter((t) => t.kind === 'leads_gone')
    .sort((a, b) => b.spendCurrent - a.spendCurrent)
    .slice(0, topN);
  const leadsBack = transitions
    .filter((t) => t.kind === 'leads_back')
    .sort((a, b) => b.leadsCurrent - a.leadsCurrent)
    .slice(0, topN);
  return { leadsGone, leadsBack, wins, worries };
}
